/**
 * Join step three: the hours the participant can spend on the outing.
 *
 * Both bounds are optional; when they are both filled the range has to be
 * usable, because the engine treats the window as a hard constraint and a
 * reversed range would silently exclude every candidate.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'

import { isValidLocalTime } from '@shared/validation'

import { ErrorAlert } from '../../../components/layout/states'
import { WizardFooter } from '../../../components/layout/wizard'
import { Card, CardContent } from '../../../components/ui/card'
import { Field, FieldGroup } from '../../../components/ui/field'
import { Input } from '../../../components/ui/input'
import { useSharedOutlet } from '../shared-layout'
import { joinStepNeighbours, joinStepPath, useJoinDraft } from './layout'

/** `HH:mm` string from the time input, or `null` when the field was cleared. */
function toBound(value: string): string | null {
  return value === '' ? null : value
}

export function JoinTimePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { shareToken } = useSharedOutlet()
  const { draft, update } = useJoinDraft(shareToken)
  const { previous, next } = joinStepNeighbours('time')

  const [error, setError] = useState<string | null>(null)

  function goNext(): void {
    const { availableTimeStart: start, availableTimeEnd: end } = draft
    const unusable =
      (start !== null && !isValidLocalTime(start)) ||
      (end !== null && !isValidLocalTime(end)) ||
      // `HH:mm` compares correctly as a string, so no date parsing is needed.
      (start !== null && end !== null && start >= end)

    if (unusable) {
      setError(t('join.timeRangeError'))
      return
    }

    setError(null)
    if (next) {
      navigate(joinStepPath(shareToken, next))
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="flex flex-col gap-3 pt-4 sm:pt-5">
          <p className="text-sm">{t('join.timeHint')}</p>

          <FieldGroup>
            <Field label={t('join.timeStartLabel')} htmlFor="availableTimeStart">
              <Input
                id="availableTimeStart"
                name="availableTimeStart"
                type="time"
                value={draft.availableTimeStart ?? ''}
                aria-invalid={Boolean(error)}
                onChange={(event) => {
                  update({ availableTimeStart: toBound(event.target.value) })
                  setError(null)
                }}
              />
            </Field>

            <Field label={t('join.timeEndLabel')} htmlFor="availableTimeEnd">
              <Input
                id="availableTimeEnd"
                name="availableTimeEnd"
                type="time"
                value={draft.availableTimeEnd ?? ''}
                aria-invalid={Boolean(error)}
                onChange={(event) => {
                  update({ availableTimeEnd: toBound(event.target.value) })
                  setError(null)
                }}
              />
            </Field>
          </FieldGroup>

          {error ? (
            <ErrorAlert message={error} />
          ) : null}
        </CardContent>
      </Card>

      <WizardFooter
        backTo={previous ? joinStepPath(shareToken, previous) : undefined}
        backLabel={t('common.back')}
        onNext={goNext}
        nextLabel={t('common.next')}
      />
    </div>
  )
}
